import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { WrenchIcon, PaletteIcon, SunIcon, StarIcon, ArrowRightIcon } from 'lucide-react';
export function Home() {
  const services = [{
    icon: <WrenchIcon size={40} data-id="element-210" />,
    title: 'Diesel Performance',
    description: "Unlock your diesel vehicle's full potential with our expert tuning and performance upgrades.",
    color: 'blue'
  }, {
    icon: <PaletteIcon size={40} data-id="element-211" />,
    title: 'Vehicle Wrapping',
    description: "Transform your vehicle's appearance with high-quality vinyl wraps in any color or design.",
    color: 'cyan'
  }, {
    icon: <SunIcon size={40} data-id="element-212" />,
    title: 'Window Tinting',
    description: 'Professional window tinting for style, privacy, and UV protection.',
    color: 'green'
  }];
  const reviews = [{
    name: 'Mike Johnson',
    rating: 5,
    text: 'Shane and his team did an incredible job on my F-350. The performance upgrade exceeded my expectations. Highly recommend!',
    date: '2 weeks ago'
  }, {
    name: 'Sarah Martinez',
    rating: 5,
    text: 'Best vehicle wrap in Colorado! The attention to detail was amazing. My truck looks absolutely stunning.',
    date: '1 month ago'
  }, {
    name: 'David Thompson',
    rating: 5,
    text: 'Professional service from start to finish. The tinting job was perfect and the price was very reasonable.',
    date: '3 weeks ago'
  }, {
    name: 'Jessica Chen',
    rating: 5,
    text: 'Shane really knows his stuff when it comes to diesel performance. My truck runs better than ever!',
    date: '1 week ago'
  }];
  const getServiceColorClasses = (color: string) => {
    switch (color) {
      case 'blue':
        return 'text-blue-400 bg-blue-500/10 border-blue-500/30';
      case 'cyan':
        return 'text-cyan-400 bg-cyan-500/10 border-cyan-500/30';
      case 'green':
        return 'text-green-400 bg-green-500/10 border-green-500/30';
      default:
        return 'text-cyan-400 bg-cyan-500/10 border-cyan-500/30';
    }
  };
  return <div className="w-full bg-black" data-id="element-213">
      <Helmet data-id="element-214">
        <title data-id="element-215">
          Western Royal Diesel - Elite Diesel Performance & Wrapping
        </title>
        <meta name="description" content="Elizabeth, Colorado's premier destination for diesel upgrades, vehicle wrapping, and professional tinting" data-id="element-216" />
      </Helmet>
      {/* Hero Section */}
      <section className="relative text-white py-20 md:py-32 overflow-hidden" data-id="element-217">
        {/* Background Image with Dark Overlay */}
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: 'url(https://uploadthingy.s3.us-west-1.amazonaws.com/swAAPqFYRKaf8cbbmasfgP/IMG_9019.jpg)'
      }} data-id="element-218">
          <div className="absolute inset-0 bg-black/70" data-id="element-219"></div>
        </div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(6,182,212,0.1),transparent_50%)]" data-id="element-220"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-221">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-cyan-400 via-blue-500 to-green-400 bg-clip-text text-transparent" data-id="element-222">
            Elite Diesel Performance
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto" data-id="element-223">
            Elizabeth, Colorado's premier destination for diesel upgrades,
            vehicle wrapping, and professional tinting
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-224">
            <Link to="/contact" className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all shadow-lg shadow-cyan-500/50 inline-flex items-center justify-center gap-2" data-id="element-225">
              Get a Quote <ArrowRightIcon size={20} data-id="element-226" />
            </Link>
            <Link to="/gallery" className="bg-gray-800 hover:bg-gray-700 text-white border border-cyan-500/50 px-8 py-4 rounded-lg text-lg font-semibold transition-all" data-id="element-227">
              View Our Work
            </Link>
          </div>
        </div>
      </section>
      {/* Services Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-black to-gray-950" data-id="element-228">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-229">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white" data-id="element-230">
            Our Services
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-231">
            {services.map((service, index) => <div key={index} className={`bg-gray-900 p-8 rounded-lg border ${getServiceColorClasses(service.color)} hover:shadow-xl transition-all hover:scale-105`} data-id="element-232">
                <div className={getServiceColorClasses(service.color).split(' ')[0] + ' mb-4'} data-id="element-233">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-bold mb-3 text-white" data-id="element-234">
                  {service.title}
                </h3>
                <p className="text-gray-400" data-id="element-235">{service.description}</p>
              </div>)}
          </div>
        </div>
      </section>
      {/* Reviews Section */}
      <section className="py-16 md:py-24 bg-gray-950" data-id="element-236">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-237">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white" data-id="element-238">
            What Our Customers Say
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-239">
            {reviews.map((review, index) => <div key={index} className="bg-gray-900 p-6 rounded-lg border border-cyan-500/20 hover:border-cyan-500/40 transition-all" data-id="element-240">
                <div className="flex items-center gap-1 mb-3" data-id="element-241">
                  {[...Array(review.rating)].map((_, i) => <StarIcon key={i} size={20} className="fill-green-400 text-green-400" data-id="element-242" />)}
                </div>
                <p className="text-gray-300 mb-4" data-id="element-243">{review.text}</p>
                <div className="flex justify-between items-center" data-id="element-244">
                  <span className="font-semibold text-cyan-400" data-id="element-245">
                    {review.name}
                  </span>
                  <span className="text-sm text-gray-500" data-id="element-246">{review.date}</span>
                </div>
              </div>)}
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-cyan-600 via-blue-600 to-green-600 text-white" data-id="element-247">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-248">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-249">
            Ready to Upgrade Your Ride?
          </h2>
          <p className="text-xl mb-8" data-id="element-250">
            Contact us today for a free consultation and pricing quote.
          </p>
          <Link to="/contact" className="bg-black hover:bg-gray-900 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors inline-block shadow-lg" data-id="element-251">
            Contact Us Now
          </Link>
        </div>
      </section>
    </div>;
}